<div class="p-6">
  <div class="flex items-center justify-between mb-4">
    <div>
      <h1 class="text-2xl font-semibold text-gray-800">Scheduled tasks</h1>
      <p class="text-sm text-gray-500" *ngIf="lastRefresh() as last">
        Last refresh: {{ last | date:'HH:mm:ss' }} — auto-refresh every 30s
      </p>
    </div>
    <div class="flex items-center gap-2">
      <button *ngIf="executedCount() > 0" type="button" (click)="clearExecuted()" [disabled]="loading()"
        class="inline-flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-50 text-red-700 text-sm rounded transition-colors">
        <i class="fas fa-trash"></i>
        Clear executed ({{ executedCount() }})
      </button>
      <button type="button" (click)="refresh()" [disabled]="loading()"
        class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded transition-colors">
        <i class="fas fa-rotate" [class.fa-spin]="loading()"></i>
        Refresh
      </button>
    </div>
  </div>

  <div *ngIf="error()" class="mb-4 px-4 py-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
    <i class="fas fa-triangle-exclamation mr-2"></i>{{ error() }}
  </div>

  <div class="bg-white shadow rounded-lg overflow-hidden">
    <table class="min-w-full divide-y divide-gray-200 text-sm">
      <thead class="bg-gray-50">
        <tr>
          <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider w-8"></th>
          <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Next run</th>
          <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
            <select [value]="selectedStatus()" (change)="selectedStatus.set($any($event.target).value)"
              (click)="$event.stopPropagation()"
              class="font-medium text-gray-500 uppercase tracking-wider bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer">
              <option value="">Status</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Running">Running</option>
              <option value="Executed:success">Executed</option>
              <option value="Executed:failed">Failed</option>
            </select>
          </th>
          <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Type</th>
          <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
            <select [value]="selectedTenant()" (change)="onTenantChange($any($event.target).value)"
              (click)="$event.stopPropagation()"
              class="font-medium text-gray-500 uppercase tracking-wider bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer">
              <option value="">Tenant</option>
              <option *ngFor="let tenant of tenantOptions()" [value]="tenant">{{ tenant }}</option>
            </select>
          </th>
          <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">
            <select [value]="selectedPosKey()" (change)="selectedPosKey.set($any($event.target).value)"
              (click)="$event.stopPropagation()"
              class="font-medium text-gray-500 uppercase tracking-wider bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer">
              <option value="">Site</option>
              <option *ngFor="let pos of posOptions()" [value]="pos.key">{{ pos.label }}</option>
            </select>
          </th>
          <th class="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Summary</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-100">
        <tr *ngIf="!loading() && filteredTasks().length === 0">
          <td colspan="7" class="px-4 py-8 text-center text-gray-400">
            <i class="fas fa-inbox text-2xl mb-2 block"></i>
            No scheduled task
          </td>
        </tr>

        <ng-container *ngFor="let task of filteredTasks()">
          <tr class="hover:bg-gray-50 cursor-pointer" (click)="toggleTask(task)">
            <td class="px-4 py-3 text-gray-400">
              <i class="fas" [class.fa-chevron-down]="expandedTaskId() === rowKey(task)"
                [class.fa-chevron-right]="expandedTaskId() !== rowKey(task)"></i>
            </td>
            <td class="px-4 py-3 whitespace-nowrap">
              <div class="font-medium" [ngClass]="task.status === 'Executed' ? 'text-gray-400' : 'text-gray-800'">
                {{ (task.status === 'Executed' ? task.executedUtc : task.nextRunUtc) | date:'yyyy-MM-dd HH:mm:ss' }}
              </div>
              <div class="text-xs text-gray-400">{{ task.status === 'Executed' ? 'executed UTC' : 'UTC' }}</div>
            </td>
            <td class="px-4 py-3">
              <span class="px-2 py-1 rounded-full text-xs font-medium" [ngClass]="statusBadgeClass(task)">
                {{ statusLabel(task) }}
              </span>
            </td>
            <td class="px-4 py-3">
              <span class="px-2 py-1 rounded-full text-xs font-medium" [ngClass]="kindBadgeClass(task.kind)">
                {{ kindLabel(task) }}
              </span>
            </td>
            <td class="px-4 py-3 text-gray-700">{{ task.tenantPrefix ?? '(all)' }}</td>
            <td class="px-4 py-3 text-gray-700">
              <span *ngIf="task.posId; else noPos">{{ task.posName ?? task.posId }}</span>
              <ng-template #noPos><span class="text-gray-400">—</span></ng-template>
            </td>
            <td class="px-4 py-3 text-gray-600">
              <ng-container [ngSwitch]="task.kind">
                <span *ngSwitchCase="'Report'">
                  {{ task.accountEmail ?? task.accountId }}
                  <span class="text-gray-400" *ngIf="task.timeZoneIanaId">· {{ task.timeZoneIanaId }}</span>
                </span>
                <span *ngSwitchCase="'AutoCheckout'">
                  <ng-container *ngIf="task.status === 'Executed'; else scheduledSummary">
                    {{ task.processedVisitCount ?? 0 }} visit(s) processed
                  </ng-container>
                  <ng-template #scheduledSummary>
                    {{ task.queuedVisitCount ?? 0 }} visit(s) in queue
                  </ng-template>
                  <span class="text-gray-400" *ngIf="task.autoCheckoutHour != null">· {{ task.autoCheckoutHour }}h</span>
                </span>
                <span *ngSwitchDefault>{{ task.taskType }}</span>
              </ng-container>
            </td>
          </tr>

          <tr *ngIf="expandedTaskId() === rowKey(task)" class="bg-gray-50">
            <td></td>
            <td colspan="6" class="px-4 py-4">
              <div class="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-xs text-gray-600">
                <div><span class="font-medium text-gray-500">Task ID:</span> {{ task.taskId }}</div>
                <div><span class="font-medium text-gray-500">Type:</span> {{ task.taskType }} ({{ task.kind }})</div>
                <div><span class="font-medium text-gray-500">Status:</span> {{ statusLabel(task) }}</div>
                <div><span class="font-medium text-gray-500">Tenant:</span> {{ task.tenantPrefix ?? '(all tenants)' }}</div>
                <div *ngIf="task.clientId"><span class="font-medium text-gray-500">Client:</span> {{ task.clientId }}</div>
                <div *ngIf="task.posId"><span class="font-medium text-gray-500">POS:</span> {{ task.posName ?? '' }} ({{ task.posId }})</div>
                <div><span class="font-medium text-gray-500">Next run UTC:</span> {{ task.nextRunUtc | date:'yyyy-MM-dd HH:mm:ss' }}</div>
                <div *ngIf="task.executedUtc"><span class="font-medium text-gray-500">Executed UTC:</span> {{ task.executedUtc | date:'yyyy-MM-dd HH:mm:ss' }}</div>
                <div *ngIf="task.lastRunError" class="text-red-600"><span class="font-medium text-red-500">Last run error:</span> {{ task.lastRunError }}</div>
                <div *ngIf="task.timeZoneIanaId"><span class="font-medium text-gray-500">Time zone:</span> {{ task.timeZoneIanaId }}</div>

                <ng-container *ngIf="task.kind === 'Report'">
                  <div><span class="font-medium text-gray-500">Account:</span> {{ task.accountEmail ?? task.accountId }} ({{ task.accountId }})</div>
                  <div><span class="font-medium text-gray-500">Period:</span> {{ task.reportPeriod }} @ {{ task.reportStartHour }}h</div>
                  <div><span class="font-medium text-gray-500">Period covered:</span>
                    {{ task.periodStartUtc | date:'yyyy-MM-dd HH:mm' }} → {{ task.periodEndUtc | date:'yyyy-MM-dd HH:mm' }} UTC
                  </div>
                </ng-container>

                <ng-container *ngIf="task.kind === 'AutoCheckout'">
                  <div><span class="font-medium text-gray-500">Checkout mode:</span> {{ checkoutModeLabel(task.autoCheckoutMode) }}</div>
                  <div><span class="font-medium text-gray-500">Checkout hour:</span> {{ task.autoCheckoutHour }}</div>
                </ng-container>
              </div>

              <div *ngIf="task.processedVisits?.length" class="mt-4">
                <div class="text-xs font-medium text-gray-500 mb-1">Processed visits ({{ task.processedVisits?.length }})</div>
                <table class="min-w-[24rem] divide-y divide-gray-200 text-xs border border-gray-200 rounded">
                  <thead class="bg-gray-100">
                    <tr>
                      <th class="px-3 py-2 text-left font-medium text-gray-500">Visit ID</th>
                      <th class="px-3 py-2 text-left font-medium text-gray-500">Due UTC</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100 bg-white">
                    <tr *ngFor="let item of task.processedVisits">
                      <td class="px-3 py-1.5">{{ item.visitId }}</td>
                      <td class="px-3 py-1.5">{{ item.dueUtc | date:'yyyy-MM-dd HH:mm:ss' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div *ngIf="task.visitQueue?.length" class="mt-4">
                <div class="text-xs font-medium text-gray-500 mb-1">Visit checkout queue ({{ task.visitQueue?.length }})</div>
                <table class="min-w-[24rem] divide-y divide-gray-200 text-xs border border-gray-200 rounded">
                  <thead class="bg-gray-100">
                    <tr>
                      <th class="px-3 py-2 text-left font-medium text-gray-500">Visit ID</th>
                      <th class="px-3 py-2 text-left font-medium text-gray-500">Due UTC</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100 bg-white">
                    <tr *ngFor="let item of task.visitQueue">
                      <td class="px-3 py-1.5">{{ item.visitId }}</td>
                      <td class="px-3 py-1.5">{{ item.dueUtc | date:'yyyy-MM-dd HH:mm:ss' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </div>
</div>